import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FaUsers, FaTable, FaUserCheck, FaMobileAlt, FaArrowRight, FaChevronDown, FaChevronUp, FaCrown, FaLock } from 'react-icons/fa';
import FormPreview from '../components/FormPreview';
import { Helmet } from 'react-helmet';
import Footer from '../components/Footer';

/* ── scroll-reveal hook ── */
function useReveal() {
    useEffect(() => {
        const els = document.querySelectorAll('.reveal');
        const obs = new IntersectionObserver(
            entries => entries.forEach(e => {
                if (e.isIntersecting) e.target.classList.add('revealed');
            }),
            { threshold: 0.12 }
        );
        els.forEach(el => obs.observe(el));
        return () => obs.disconnect();
    }, []);
}

/* ── floating petals ── */
const Petals = () => {
    const petals = [
        { left: '8%', s: 12, d: 0, dur: 14 },
        { left: '22%', s: 8, d: 2.3, dur: 17 },
        { left: '38%', s: 16, d: 1, dur: 12 },
        { left: '55%', s: 10, d: 4, dur: 15 },
        { left: '70%', s: 14, d: 0.6, dur: 11 },
        { left: '84%', s: 9, d: 3, dur: 16 },
        { left: '93%', s: 13, d: 1.7, dur: 13 },
    ];
    return (
        <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
            {petals.map((p, i) => (
                <div
                    key={i}
                    style={{
                        position: 'absolute',
                        left: p.left,
                        bottom: '-5%',
                        width: p.s,
                        height: p.s * 1.5,
                        borderRadius: '50% 0 50% 0',
                        background: 'linear-gradient(135deg, rgba(251,191,200,0.8), rgba(103,232,249,0.3))',
                        animation: `petalFloat ${p.dur}s linear ${p.d}s infinite`,
                        pointerEvents: 'none',
                    }}
                />
            ))}
        </div>
    );
};

/* ── stat counter ── */
const StatCounter = ({ num, unit, label }) => {
    const [count, setCount] = useState(0);
    const ref = useRef(null);
    const target = parseInt(num);

    useEffect(() => {
        const obs = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                let start = 0;
                const step = Math.ceil(target / 40);
                const timer = setInterval(() => {
                    start += step;
                    if (start >= target) { setCount(target); clearInterval(timer); }
                    else setCount(start);
                }, 30);
                obs.disconnect();
            }
        }, { threshold: 0.3 });
        if (ref.current) obs.observe(ref.current);
        return () => obs.disconnect();
    }, [target]);

    return (
        <div ref={ref} className="text-center">
            <div className="text-3xl font-bold text-gray-800">
                {count}<span className="text-pink-500 text-2xl">{unit}</span>
            </div>
            <div className="text-xs uppercase tracking-widest text-gray-400 mt-1">{label}</div>
        </div>
    );
};

const Home = () => {
    const [openDropdown, setOpenDropdown] = useState(null);
    const [scrolled, setScrolled] = useState(false);
    const [lang, setLang] = useState(() => localStorage.getItem('lang') || 'hr');
    useReveal();

    useEffect(() => {
        const fn = () => setScrolled(window.scrollY > 30);
        window.addEventListener('scroll', fn);
        return () => window.removeEventListener('scroll', fn);
    }, []);

    return (
        <>
            <style>{`
                @keyframes petalFloat {
                    0%   { transform: translateY(0) rotate(0deg); opacity: 0; }
                    8%   { opacity: 0.8; }
                    92%  { opacity: 0.3; }
                    100% { transform: translateY(-110vh) rotate(480deg); opacity: 0; }
                }
                @keyframes floatCard {
                    0%, 100% { transform: translateY(0px); }
                    50%      { transform: translateY(-8px); }
                }
                @keyframes shimmerBtn {
                    0%   { background-position: -200% center; }
                    100% { background-position: 200% center; }
                }
                .float-card { animation: floatCard 4s ease-in-out infinite; }
                .reveal {
                    opacity: 0;
                    transform: translateY(24px);
                    transition: opacity 0.7s ease, transform 0.7s ease;
                }
                .reveal.revealed { opacity: 1; transform: translateY(0); }
                .reveal-delay-1 { transition-delay: 0.1s; }
                .reveal-delay-2 { transition-delay: 0.2s; }
                .reveal-delay-3 { transition-delay: 0.3s; }
                .nav-blur {
                    backdrop-filter: blur(14px);
                    -webkit-backdrop-filter: blur(14px);
                }
                .feature-card-hover {
                    transition: transform 0.3s ease, box-shadow 0.3s ease;
                }
                .feature-card-hover:hover {
                    transform: translateY(-6px) scale(1.02);
                    box-shadow: 0 20px 40px rgba(236,72,153,0.15);
                }
            `}</style>

            <Helmet>
                <title>Weddinfy — Digitalni raspored gostiju za vaše vjenčanje</title>
                <meta name="description" content="Jednostavan sustav za raspoređivanje gostiju na svadbi. Gosti biraju stol putem koda, vi imate potpunu kontrolu." />
            </Helmet>

            <div className="min-h-screen bg-gradient-to-br from-cyan-300 via-white to-pink-400 flex flex-col">
                <main className="flex-1">

                    {/* ── NAV ── */}
                    <div className={`flex justify-between items-center px-4 py-4 lg:px-10 relative z-50 transition-all duration-300 ${scrolled ? 'nav-blur bg-white/70 shadow-sm' : ''}`}
                        style={{ position: 'sticky', top: 0 }}>
                        <div className='flex justify-center items-center gap-2'>
                            <img src="/logo2.png" className="w-12 h-12" alt="Weddinfy" />
                            <h1 className="text-xl font-semibold text-gray-800">Weddinfy</h1>
                        </div>
                        <div className="flex items-center space-x-4">
                            {/* Language Dropdown */}
                            <div className="relative" tabIndex={0} onBlur={() => setOpenDropdown(null)}>
                                <div
                                    className="bg-white border border-gray-500 rounded-xl px-2 cursor-pointer flex items-center"
                                    onClick={() => setOpenDropdown(prev => prev === 'language' ? null : 'language')}
                                >
                                    <span className="text-gray-800 uppercase">
                                        {lang === 'hr' && '🇭🇷'}
                                        {lang === 'en' && '🇬🇧'}
                                        {lang === 'de' && '🇩🇪'}
                                    </span>
                                    {openDropdown === 'language'
                                        ? <FaChevronUp className="ml-2 text-gray-600" />
                                        : <FaChevronDown className="ml-2 text-gray-600" />
                                    }
                                </div>
                                {openDropdown === 'language' && (
                                    <div className="absolute right-0 mt-1 z-50 w-full min-w-[130px] bg-white border border-gray-500 rounded-xl shadow-lg">
                                        {[
                                            { code: 'hr', label: 'Hrvatski', flag: '🇭🇷' },
                                            { code: 'en', label: 'English', flag: '🇬🇧' },
                                            { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
                                        ].map((l) => (
                                            <button
                                                key={l.code}
                                                type="button"
                                                onMouseDown={() => {
                                                    setLang(l.code);
                                                    localStorage.setItem('lang', l.code);
                                                    setOpenDropdown(null);
                                                }}
                                                className="w-full text-left px-4 py-2 text-gray-800 hover:bg-gray-100 rounded-xl flex items-center gap-2"
                                            >
                                                <span className="text-xl">{l.flag}</span>
                                                <span>{l.label}</span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <Link to="/login" className="text-gray-800 hover:underline">
                                Prijava
                            </Link>
                        </div>
                    </div>

                    {/* ── HERO ── */}
                    <section className="py-10 md:py-20 relative overflow-hidden">
                        <Petals />
                        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                            <div className="grid md:grid-cols-2 gap-12 items-center">
                                <div className="text-center md:text-left">
                                    {/* eyebrow */}
                                    <div className="inline-flex items-center gap-2 bg-white/70 border border-pink-200 rounded-full px-4 py-1.5 mb-5 text-xs tracking-widest uppercase text-pink-500 font-medium">
                                        <span>💍</span> Digitalni raspored gostiju
                                    </div>
                                    <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6 leading-tight">
                                        Neka gosti sami odaberu stol — vi imate kontrolu nad svime
                                    </h1>
                                    <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                                        Svaki gost dobiva osobni kod i sam rezervira svoje mjesto. Vi pratite, prilagođavate i zaključavate raspored — te ga na kraju preuzimate kao PDF spreman za print.
                                    </p>

                                    <div className="flex justify-center md:justify-start gap-8 mb-8 py-5 border-t border-b border-pink-100">
                                        <StatCounter num="2" unit=" min" label="Postavljanje" />
                                        <StatCounter num="100" unit="%" label="Vaša kontrola" />
                                        <StatCounter num="1" unit=" klik" label="PDF export" />
                                    </div>

                                    <div className="flex flex-col sm:flex-row gap-4">
                                        <Link
                                            to="/register"
                                            className="bg-gradient-to-r from-rose-500 to-pink-600 hover:from-rose-600 hover:to-pink-700 shadow-lg shadow-rose-500/30 text-white px-8 py-4 rounded-xl hover:shadow-xl hover:shadow-rose-500/40 hover:-translate-y-0.5 transition-all flex items-center justify-center font-medium"
                                        >
                                            Kreirajte svoju svadbu
                                            <FaArrowRight className="ml-2 h-5 w-5" />
                                        </Link>
                                        <Link
                                            to="/guest"
                                            className="border-2 border-pink-400 text-pink-500 px-8 py-4 rounded-xl hover:bg-pink-500 hover:text-white transition-all flex items-center justify-center font-medium"
                                        >
                                            Pogledajte demo
                                        </Link>
                                    </div>
                                </div>

                                {/* hero image with float animation */}
                                <div className="hidden md:flex justify-center">
                                    <div className="float-card relative">
                                        <img
                                            src="/img2.jpg"
                                            alt="Vjenčanje"
                                            className="w-full rounded-2xl shadow-2xl shadow-pink-200"
                                        />
                                        {/* floating badge */}
                                        <div className="absolute -bottom-4 -left-4 bg-white rounded-2xl shadow-xl px-4 py-3 flex items-center gap-2 border border-pink-100">
                                            <span className="text-2xl">👑</span>
                                            <div>
                                                <div className="text-xs font-semibold text-gray-700">Admin kontrola</div>
                                                <div className="text-xs text-gray-400">Uvijek imate zadnju riječ</div>
                                            </div>
                                        </div>
                                        <div className="absolute -top-4 -right-4 bg-white rounded-2xl shadow-xl px-4 py-3 flex items-center gap-2 border border-cyan-100">
                                            <span className="text-2xl">🔑</span>
                                            <div>
                                                <div className="text-xs font-semibold text-gray-700">Pristupni kod</div>
                                                <div className="text-xs text-gray-400">Bez registracije</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* ── FEATURES ── */}
                    <section className="py-16">
                        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                            <div className="text-center mb-12 reveal">
                                <h2 className="text-3xl font-bold text-gray-800 mb-3">
                                    Sve što trebate za savršen dan
                                </h2>
                                <p className="text-gray-500 max-w-xl mx-auto">
                                    Od trenutka kada pošaljete kod gostu, do finalnog zaključavanja rasporeda — sve je u vašim rukama.
                                </p>
                            </div>
                            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                                {[
                                    {
                                        icon: <FaTable className="h-8 w-8" />,
                                        title: 'Stolovi i gosti',
                                        desc: 'Kreirate stolove po imenu i broju mjesta, zatim dodate goste. Sustav automatski generira osobni pristupni kod za svakog gosta.',
                                        color: 'text-cyan-500',
                                        bg: 'bg-cyan-50',
                                        delay: '',
                                    },
                                    {
                                        icon: <FaUsers className="h-8 w-8" />,
                                        title: 'Privatni stolovi',
                                        desc: 'Kreirajte stolove koje gosti ne vide — za VIP uzvanike, obitelj ili posebne rasporede. Skriveni raspored samo za vas.',
                                        color: 'text-blue-500',
                                        bg: 'bg-blue-50',
                                        delay: 'reveal-delay-1',
                                    },
                                    {
                                        icon: <FaMobileAlt className="h-8 w-8" />,
                                        title: 'Rezervacija kodom',
                                        desc: 'Svaki gost dobiva osobni pristupni kod od vas. Unese ga, odabere stol — gotovo. Bez dugih formi, bez lozinki.',
                                        color: 'text-violet-500',
                                        bg: 'bg-violet-50',
                                        delay: 'reveal-delay-2',
                                    },
                                    {
                                        icon: <FaCrown className="h-8 w-8" />,
                                        title: 'Vi imate zadnju riječ',
                                        desc: 'Premjestite gosta, uklonite rezervaciju, rasporedite ručno. Bez obzira što su gosti odabrali — admin override je uvijek dostupan.',
                                        color: 'text-pink-500',
                                        bg: 'bg-pink-50',
                                        delay: '',
                                        highlight: true,
                                    },
                                    {
                                        icon: <FaLock className="h-8 w-8" />,
                                        title: 'Zaključavanje rasporeda',
                                        desc: 'Zaključajte pojedini stol ručno čim ste zadovoljni rasporedom — gosti na njemu više ne mogu mijenjati mjesta. Sve rezervacije se automatski zamrzavaju onoliko dana prije svadbe koliko vi odredite u postavkama.',
                                        color: 'text-rose-500',
                                        bg: 'bg-rose-50',
                                        delay: 'reveal-delay-1',
                                    },
                                    {
                                        icon: <FaUserCheck className="h-8 w-8" />,
                                        title: 'PDF spreman za print',
                                        desc: 'Kada je raspored finaliziran, preuzmite ga kao PDF. Uredni prikaz stolova spreman za ispis i postavljanje na dan svadbe.',
                                        color: 'text-emerald-500',
                                        bg: 'bg-emerald-50',
                                        delay: 'reveal-delay-2',
                                    },
                                ].map((feature, index) => (
                                    <div
                                        key={index}
                                        className={`reveal ${feature.delay} feature-card-hover bg-white border p-6 rounded-2xl transition-all duration-300 ${feature.highlight
                                            ? 'border-pink-300 shadow-lg shadow-pink-100 ring-1 ring-pink-200'
                                            : 'border-gray-200'
                                            }`}
                                    >
                                        {feature.highlight && (
                                            <div className="text-[10px] tracking-widest uppercase text-pink-500 font-semibold mb-2 text-center">
                                                ★ Najvažnija funkcija
                                            </div>
                                        )}
                                        <div className={`${feature.bg} w-16 h-16 rounded-2xl flex items-center justify-center mb-4 mx-auto ${feature.color}`}>
                                            {feature.icon}
                                        </div>
                                        <h3 className="text-xl font-semibold text-gray-800 text-center mb-2">
                                            {feature.title}
                                        </h3>
                                        <p className="text-gray-500 text-center text-sm leading-relaxed">
                                            {feature.desc}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>

                    {/* ── HOW IT WORKS ── */}
                    <section className="py-16">
                        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                            <div className="grid md:grid-cols-2 gap-12 items-center">
                                <div className="reveal">
                                    <div className="inline-flex items-center gap-2 bg-white/70 border border-cyan-200 rounded-full px-4 py-1.5 mb-4 text-xs tracking-widest uppercase text-cyan-600 font-medium">
                                        Kako funkcionira
                                    </div>
                                    <h2 className="text-3xl font-bold text-gray-800 mb-8">
                                        Kako funkcionira u praksi
                                    </h2>
                                    <div className="space-y-4">
                                        {[
                                            'Kreirate svadbu i postavite stolove s brojem mjesta',
                                            'Dodate goste — svaki automatski dobiva jedinstven pristupni kod',
                                            'Šaljete gostu poruku s linkom i kodom jednim klikom',
                                            'Gost se prijavi i odabere slobodan stol',
                                            'Zaključate raspored i preuzmete ga kao PDF spreman za print',
                                        ].map((stepText, index) => (
                                            <div
                                                key={index}
                                                className="flex items-center p-4 bg-white rounded-xl border border-gray-200 hover:border-pink-300 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 group"
                                            >
                                                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-rose-400 to-pink-500 flex items-center justify-center text-white text-sm font-bold mr-4 flex-shrink-0 group-hover:scale-110 transition-transform">
                                                    {index + 1}
                                                </div>
                                                <p className="text-gray-700">{stepText}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className="order-first md:order-last reveal reveal-delay-1">
                                    <FormPreview />
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* ── CODE ACCESS HIGHLIGHT ── */}
                    <section className="py-16">
                        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                            <div className="bg-gradient-to-r from-gray-800 to-gray-900 rounded-3xl p-10 md:p-14 relative overflow-hidden">
                                {/* decorative blobs */}
                                <div className="absolute top-0 right-0 w-64 h-64 bg-pink-500/10 rounded-full blur-3xl pointer-events-none" />
                                <div className="absolute bottom-0 left-0 w-48 h-48 bg-cyan-500/10 rounded-full blur-3xl pointer-events-none" />

                                <div className="relative z-10 grid md:grid-cols-2 gap-10 items-center">
                                    <div className="reveal">
                                        <div className="text-xs tracking-widest uppercase text-pink-400 font-medium mb-3">
                                            Jednostavan pristup za goste
                                        </div>
                                        <h2 className="text-3xl md:text-4xl font-bold text-white mb-5 leading-tight">
                                            Svaki gost dobiva<br />
                                            <span className="text-pink-400">svoj osobni kod.</span>
                                        </h2>
                                        <p className="text-white/60 text-sm leading-relaxed mb-5">
                                            Vi šaljete gostu poruku jednim klikom — sadrži njegovo ime, link za prijavu i pristupni kod. Gost klikne link, unese kod i odabere stol. Nema lozinki koje se zaboravljaju.
                                        </p>
                                        <ul className="space-y-3">
                                            {[
                                                'Poruka sadrži ime gosta, link i pristupni kod',
                                                'Šaljete putem WhatsAppa, Vibera ili SMS-a — jednim klikom',
                                                'Gost prijavu obavi za nekoliko sekundi',
                                                'Kod možete deaktivirati u bilo kojem trenutku',
                                            ].map((item, i) => (
                                                <li key={i} className="flex items-center gap-3 text-white/70 text-sm">
                                                    <span className="w-5 h-5 rounded-full bg-pink-500/25 border border-pink-400/40 flex items-center justify-center text-pink-300 text-[10px] flex-shrink-0">✓</span>
                                                    {item}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                    <div className="reveal reveal-delay-1 flex justify-center">
                                        <div className="bg-white/8 border border-white/12 rounded-2xl p-7 text-center w-full max-w-xs"
                                            style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)' }}>
                                            <div className="text-[10px] tracking-widest uppercase text-white/40 mb-4">Unesi pristupni kod</div>
                                            <div className="flex gap-2 justify-center mb-5">
                                                {['S', 'V', 'A', 'D', '8', '4'].map((c, i) => (
                                                    <div key={i} style={{
                                                        width: 40, height: 50,
                                                        background: 'rgba(255,255,255,0.09)',
                                                        border: '1.5px solid rgba(236,72,153,0.4)',
                                                        borderRadius: 8,
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                        fontSize: '1.4rem', color: '#fce7f3', fontWeight: 600,
                                                    }}>
                                                        {c}
                                                    </div>
                                                ))}
                                            </div>
                                            <button className="w-full bg-gradient-to-r from-rose-500 to-pink-600 text-white text-sm font-medium py-3 rounded-xl hover:-translate-y-0.5 transition-transform shadow-lg shadow-rose-500/30">
                                                Rezerviraj stol →
                                            </button>
                                            <p className="text-white/25 text-xs mt-3">Kod ste primili od mladenaca</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* ── CTA ── */}
                    <section className="py-16">
                        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                            <div className="reveal">
                                <div className="text-3xl mb-4 tracking-widest">🌸 🌼 🌸</div>
                                <h2 className="text-3xl font-bold text-gray-800 mb-4">
                                    Vaš poseban dan zaslužuje savršen raspored
                                </h2>
                                <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                                    Postavite svadbu za manje od 2 minute. Gosti se brinu o svom stolu, vi se brinete o svemu ostalom.
                                </p>
                                <Link
                                    to="/register"
                                    className="inline-flex items-center bg-gradient-to-r from-rose-500 to-pink-600 hover:from-rose-600 hover:to-pink-700 shadow-lg shadow-rose-500/30 text-white px-10 py-4 rounded-xl hover:shadow-xl hover:shadow-rose-500/40 hover:-translate-y-0.5 transition-all text-lg font-medium"
                                >
                                    Kreirajte svoju svadbu
                                    <FaArrowRight className="ml-2 h-5 w-5" />
                                </Link>
                            </div>
                        </div>
                    </section>
                </main>

                <footer>
                    <Footer />
                </footer>
            </div>
        </>
    );
};

export default Home;
